import { Deal } from 'models/deals'
import { DealReport } from 'models/deals_reports'
import { SalesStage } from 'models/sales_stages'
import { User } from 'models/user'
import { formatInteger } from 'utils/formatting'
import { CalendarIcon, PersonIcon } from '@radix-ui/react-icons'
import { HiOutlineQuestionMarkCircle } from 'react-icons/hi'
import { PiPencilSimpleLine } from 'react-icons/pi'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { SalesStageProgress } from './SalesStageProgress'
import apiService from 'services/api'
import { toast } from 'react-toastify'
import getStageColor from 'utils/getStageColor'
import { useMutation, useQueryClient } from '@tanstack/react-query'

export type DealOrReport = Deal | DealReport

function isDealReport(deal: DealOrReport): deal is DealReport {
  return 'cc_doors_cnt' in deal || 'sales_stage_id' in deal
}

interface DealCardProps<T extends DealOrReport> {
  deal: T
  salesStages?: SalesStage[]
  users?: User[]
  onEdit?: (deal: T) => void
}

export function DealCard<T extends DealOrReport>({
  deal,
  salesStages,
  users,
  onEdit,
}: DealCardProps<T>) {
  const api = apiService()
  const queryClient = useQueryClient()
  const { preferences } = usePreferences()
  const tooltips: Record<string, any> = preferences?.tooltips || {}

  // Normalize field access for both Deal and DealReport types
  const accountOwnerId = isDealReport(deal)
    ? deal.account_owner_id
    : deal.account_owner
  const salesStageId = isDealReport(deal)
    ? deal.sales_stage_id
    : deal.sales_stage
  const totalDoors = isDealReport(deal)
    ? deal.cc_doors_cnt
    : deal.contact_company?.doors_cnt
  const dealName = isDealReport(deal) ? deal.deal_name : deal.name

  // Find the account owner
  const accountOwner = users?.find((u) => u.id === accountOwnerId)
  const accountOwnerName = accountOwner
    ? `${accountOwner.first_name} ${accountOwner.last_name}`
    : 'No Owner'

  // Calculate days since last modified
  const daysSinceModified = deal.days_since_deal_last_modified || 0

  // Format the modified date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })
  }

  // Get door count data once
  const selectedDoors = deal.doors_count || totalDoors
  const doorCountData = {
    selectedDoors: formatInteger(selectedDoors),
    totalDoors: formatInteger(totalDoors),
    locationText: totalDoors !== 1 ? 'locations' : 'location',
  }

  const updateStageMutation = useMutation({
    mutationFn: async (newStageId: number) => {
      return api.editDeal(deal.id, { sales_stage: newStageId })
    },
    onMutate: async (newStageId: number) => {
      await queryClient.cancelQueries({ queryKey: ['deals'] })
      await queryClient.cancelQueries({ queryKey: ['report-deals'] })

      const previousQueries: any[] = []
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['deals'] })
        .forEach((query) => {
          previousQueries.push({
            queryKey: query.queryKey,
            data: query.state.data,
          })
        })
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['report-deals'] })
        .forEach((query) => {
          previousQueries.push({
            queryKey: query.queryKey,
            data: query.state.data,
          })
        })

      queryClient.setQueriesData({ queryKey: ['deals'] }, (old: any) => {
        if (!old?.results) return old
        return {
          ...old,
          results: old.results.map((d: Deal) =>
            d.id === deal.id ? { ...d, sales_stage: newStageId } : d
          ),
        }
      })
      queryClient.setQueriesData({ queryKey: ['report-deals'] }, (old: any) => {
        if (!old?.results) return old
        return {
          ...old,
          results: old.results.map((d: DealReport) =>
            d.id === deal.id ? { ...d, sales_stage_id: newStageId } : d
          ),
        }
      })

      return { previousQueries }
    },
    onError: (_err, _newStageId, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      toast.error('Failed to update deal stage. Please try again.')
    },
    onSuccess: (_data, newStageId) => {
      const newStage = salesStages?.find((s) => s.id === newStageId)
      toast.success(
        `Deal moved to ${newStage?.stage || 'new stage'} successfully!`
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      queryClient.invalidateQueries({ queryKey: ['report-deals'] })
    },
  })

  const handleStageChange = (newStageId: number) => {
    updateStageMutation.mutate(newStageId)
  }

  const getAccentColor = () => {
    if (!salesStageId || !salesStages) return '#D3935D'
    const stage = salesStages.find((s) => s.id === salesStageId)
    if (!stage) return '#D3935D'
    const [color] = getStageColor(stage.win_rate || 0)
    return color
  }

  return (
    <div className="flex rounded-lg border border-[#E2E2E2] bg-white shadow-sm">
      {/* Left colored accent bar - 8px width, color matches current stage */}
      <div
        className="w-2 rounded-l-lg transition-colors duration-200"
        style={{ backgroundColor: getAccentColor() }}
      />

      {/* Main content - flexible width with minimum */}
      <div className="p-2 w-full">
        <div className="flex flex-col gap-2.5 px-1 pb-1">
          {/* Top section: Title and metadata */}
          <div className="flex flex-col gap-2">
            {/* Title row with proper padding */}
            <div className="flex justify-between">
              <h3
                className="text-base font-semibold uppercase leading-[2em] text-[#191D23]"
                style={{ lineHeight: '2em' }}
              >
                {deal.deal_display_name || dealName || 'Untitled Deal'}
              </h3>
              <button
                onClick={() => onEdit?.(deal)}
                className="flex h-6 w-6 items-center justify-center rounded bg-new-company-pistachio-light transition-colors hover:bg-gray-50"
                aria-label="Edit deal"
              >
                <PiPencilSimpleLine className="h-3.5 w-3.5 text-new-company-green-dark" />
              </button>
            </div>

            {/* Metadata badges row */}
            <div className="flex h-5 items-center gap-2">
              {/* Updated date - no background, just icon + text */}
              <div className="flex items-center gap-1">
                <CalendarIcon className="h-3 w-3 text-[#6E6E6E]" />
                <span
                  className="text-[10px] font-light leading-[1.8em] text-[#191D23]"
                  style={{ lineHeight: '1.8em' }}
                >
                  Updated {deal.modified ? formatDate(deal.modified) : 'N/A'}
                </span>
              </div>

              {/* Days since modified badge */}
              <div className="flex h-5 items-center gap-1 rounded-full border border-[#E2E2E2] bg-[#FAFAFA] px-1.5 py-0.5">
                <div className="h-2 w-2 rounded-full bg-[#D43D51]" />
                <span
                  className="text-[10px] font-light leading-[1em] text-[#191D23]"
                  style={{ lineHeight: '1em' }}
                >
                  {daysSinceModified} days
                </span>
              </div>

              {/* Account owner badge */}
              <div className="flex h-5 items-center gap-1 rounded-full border border-[#E2E2E2] bg-[#FAFAFA] px-1.5 py-0.5">
                <PersonIcon className="h-3 w-3 text-[#6E6E6E]" />
                <span
                  className={`text-[10px] leading-[1em] text-[#191D23] ${accountOwner ? 'font-semibold' : 'font-normal'}`}
                  style={{ lineHeight: '1em' }}
                >
                  {accountOwnerName}
                </span>
              </div>
            </div>
          </div>

          {/* Divider - 1px height */}
          <div className="h-px w-full bg-[#E2E2E2]" />

          {/* Bottom section: Stage, Product, Size */}
          <div className="flex rounded-lg">
            {/* Labels column - 64px width */}
            <div className="flex w-16 flex-col justify-center gap-2">
              <div className="flex h-5 items-center gap-1">
                <span
                  className="text-xs font-medium text-[#191D23]"
                  style={{ lineHeight: '16px' }}
                >
                  Stage
                </span>
                {tooltips.deals_sales_stage ? (
                  <HelpTooltip
                    title={tooltips.deals_sales_stage?.title}
                    content={tooltips.deals_sales_stage?.content}
                    link={{
                      text: tooltips.deals_sales_stage?.link_text,
                      href: tooltips.deals_sales_stage?.link,
                    }}
                    icon={
                      <HiOutlineQuestionMarkCircle className="h-[10px] w-[10px] text-[#6E6E6E]" />
                    }
                  />
                ) : (
                  <HiOutlineQuestionMarkCircle className="h-[10px] w-[10px] text-[#6E6E6E]" />
                )}
              </div>
              <div className="flex h-5 items-center gap-1">
                <span
                  className="text-xs font-medium text-[#191D23]"
                  style={{ lineHeight: '16px' }}
                >
                  Product
                </span>
                {tooltips.deals_product ? (
                  <HelpTooltip
                    title={tooltips.deals_product?.title}
                    content={tooltips.deals_product?.content}
                    link={{
                      text: tooltips.deals_product?.link_text,
                      href: tooltips.deals_product?.link,
                    }}
                    icon={
                      <HiOutlineQuestionMarkCircle className="h-[10px] w-[10px] text-[#6E6E6E]" />
                    }
                  />
                ) : (
                  <HiOutlineQuestionMarkCircle className="h-[10px] w-[10px] text-[#6E6E6E]" />
                )}
              </div>
              <div className="flex h-5 items-center gap-1">
                <span
                  className="text-xs font-medium text-[#191D23]"
                  style={{ lineHeight: '16px' }}
                >
                  Size
                </span>
                {tooltips.deals_doors_count ? (
                  <HelpTooltip
                    title={tooltips.deals_doors_count?.title}
                    content={tooltips.deals_doors_count?.content}
                    link={{
                      text: tooltips.deals_doors_count?.link_text,
                      href: tooltips.deals_doors_count?.link,
                    }}
                    icon={
                      <HiOutlineQuestionMarkCircle className="h-[10px] w-[10px] text-[#6E6E6E]" />
                    }
                  />
                ) : (
                  <HiOutlineQuestionMarkCircle className="h-[10px] w-[10px] text-[#6E6E6E]" />
                )}
              </div>
            </div>

            {/* Values column */}
            <div className="flex flex-1 flex-col justify-center gap-2 pl-2">
              {/* Stage - Visual progress indicator */}
              <div className="flex h-6 items-center">
                <SalesStageProgress
                  currentStageId={salesStageId}
                  salesStages={salesStages}
                  onStageChange={handleStageChange}
                />
              </div>

              {/* Product badge */}
              <div className="flex h-5 items-center">
                <div className="flex items-center rounded-full border border-[#E2E2E2] bg-[#FAFAFA] px-1.5 py-1">
                  <span
                    className="text-[10px] font-semibold leading-[1em] tracking-[0.03em] text-[#191D23]"
                    style={{ lineHeight: '1em', letterSpacing: '0.03em' }}
                  >
                    {deal.product_name || 'Unknown Product'}
                  </span>
                </div>
              </div>

              {/* Size badges */}
              <div className="flex h-5 items-center gap-2 pr-2">
                {/* Revenue badge */}
                {deal.annual_revenue || deal.monthly_revenue_predicted ? (
                  <div className="flex items-center gap-0.5 rounded-full border border-[#E2E2E2] bg-[#FAFAFA] px-2 py-1">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="flex-shrink-0"
                    >
                      <path
                        d="M6 0.75C3.10312 0.75 0.75 3.10312 0.75 6C0.75 8.89688 3.10312 11.25 6 11.25C8.89688 11.25 11.25 8.89688 11.25 6C11.25 3.10312 8.89688 0.75 6 0.75ZM6.98438 8.25C6.98438 8.34375 6.92812 8.4375 6.79688 8.4375H6.1875C6.09375 8.4375 6 8.38125 6 8.25V7.96875C5.62031 7.93125 5.24062 7.81875 4.93125 7.63125C4.85625 7.59375 4.8 7.50937 4.8 7.42812L4.91719 6.89062C4.91719 6.77812 5.01094 6.72188 5.08594 6.75938C5.43281 6.98438 5.77969 7.11562 6.14062 7.11562C6.54844 7.11562 6.76875 6.93281 6.76875 6.68438C6.76875 6.42656 6.58125 6.27188 6.05625 6.10781C5.35781 5.90156 4.81875 5.59688 4.81875 4.93125C4.81875 4.35469 5.24062 3.93281 5.99062 3.80156V3.52969C5.99062 3.43594 6.08437 3.32812 6.1875 3.32812H6.79688C6.92812 3.32812 6.98438 3.42188 6.98438 3.52969V3.7875C7.3125 3.825 7.60781 3.91875 7.87969 4.06875C7.96406 4.11562 8.00625 4.19062 8.00625 4.27031L7.91719 4.76719C7.91719 4.88437 7.8 4.93594 7.72031 4.89844C7.42031 4.72969 7.11094 4.63594 6.74062 4.63594C6.29531 4.63594 6.14062 4.84219 6.14062 5.0625C6.14062 5.28281 6.34688 5.4375 6.88594 5.6109C7.60312 5.84531 8.08594 6.15 8.08594 6.77344C8.09062 7.40625 7.66875 7.875 6.98438 8.00625V8.25Z"
                        fill="#087443"
                      />
                    </svg>
                    <span
                      className="text-[10px] font-bold leading-[1em] text-[#191D23]"
                      style={{ lineHeight: '1em' }}
                    >
                      {formatInteger(
                        deal.annual_revenue ||
                          deal.monthly_revenue_predicted ||
                          0
                      )}
                    </span>
                  </div>
                ) : null}

                {/* Door count badge */}
                <div className="flex items-center rounded-full border border-[#E2E2E2] bg-[#FAFAFA] px-1.5 py-1">
                  <span
                    className="text-[10px] leading-[1em] tracking-[0.03em] text-[#191D23]"
                    style={{ lineHeight: '1em', letterSpacing: '0.03em' }}
                  >
                    <span className="font-bold">
                      {doorCountData.selectedDoors}
                    </span>
                    {' / '}
                    {doorCountData.totalDoors} {doorCountData.locationText}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
